/**
 * IMPORTANT: The appConfig defaults in this file must be kept in sync with:
 * /home/timum/Timum/core/public/javascripts/react/timum_react_components/src/components/publicationView/appConfigDefaults.js
 *
 * When changing appConfig defaults in either file, mirror the changes in the other.
 */
'use strict';
import React from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import ScopableDialog from './components/base/ScopableDialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';

import ClosableDialogTitle from './components/base/ClosableDialogTitle';
import { Container } from '@mui/material';
import { alpha, emphasize } from '@mui/material/styles';
import { controlRadius } from './styles/tokens';

const borderRadius = 6;
const fontSizeBase = 14;
const fontFamily = '"Figtree","Helvetica Neue", Helvetica, Arial, sans-serif';
// const lineHeightBase = 1.428571429;
// const lineHeightComputed = Math.floor(fontSizeBase * lineHeightBase);

const fontSizeH1 = Math.floor(fontSizeBase * 2.6); // 36
const fontSizeH2 = Math.floor(fontSizeBase * 2.3); // 32
const fontSizeH3 = Math.floor(fontSizeBase * 1.9); // 26
const fontSizeH4 = Math.floor(fontSizeBase * 1.5); // 21
const fontSizeH5 = Math.floor(fontSizeBase * 1.15); // 16
const fontSizeH6 = Math.floor(fontSizeBase); // 14

/**
 * Standard field names that are part of the default appConfig.
 * These fields can be toggled on/off but not deleted entirely.
 */
export const STANDARD_FIELD_NAMES = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'message',
  'agbs',
  'locale',
];

// Outlined-input outline colours, as alpha steps off the theme's OWN text
// colour — replacing MUI's mode-specific literals (`rgba(0,0,0,.23)` light /
// `rgba(255,255,255,.23)` dark) and its full-strength `text.primary` on hover.
//
// Two things this fixes at once: the outline tracks whatever text colour the
// customer chose instead of assuming black or white, and resting -> hover
// becomes a STEP (.23 -> .5) rather than a slam from a faint line to
// full-strength text — a glare on dark themes, heavy-handed on light ones.
// Focus is left alone: MUI already uses themed `primary.main` at 2px, and that
// is meant to be the loudest state.
//
// Every OutlinedInput instance renders this callback, and the booking form has
// a dozen fields plus the un-restylable mui-tel-input, so the result is cached
// per theme (themes are stable objects — same reasoning as DomHandler's
// getThemeWithPortalsCached).
const NOTCHED_OUTLINE = '& .MuiOutlinedInput-notchedOutline';
const HOVER_NOTCHED_OUTLINE =
  '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline';
const EMPTY_OUTLINE = Object.freeze({});
const outlineCache = new WeakMap();

function outlinedInputOutline(theme) {
  const cached = outlineCache.get(theme);
  if (cached) return cached;
  let style;
  try {
    style = {
      [NOTCHED_OUTLINE]: {
        borderColor: alpha(theme.palette.text.primary, 0.23),
      },
      [HOVER_NOTCHED_OUTLINE]: {
        borderColor: alpha(theme.palette.text.primary, 0.5),
      },
    };
  } catch {
    // alpha() parses the colour in JS; on the CssVars path palette values are
    // `var(--…)` strings it cannot read. Skip the override rather than throw
    // and take the rest of the theme down.
    style = EMPTY_OUTLINE;
  }
  outlineCache.set(theme, style);
  return style;
}

/*
 * Default configuration
 *
 */
export const primary = {
  appConfig: {
    host: 'https://www.timum.de',

    /*
     * Valid values for resource_channel attribute:
     *  RESOURCE_PUBLIC,
     *  RESOURCE_EXCLUSIVE
     *  RESOURCE_REFERENCE
     *  CALENDAR_PUBLIC
     */
    resource_channel: 'RESOURCE_REFERENCE',

    // either fullCalendar or pureListView or detailsListView
    calendarFrontend: 'detailsListView',

    // settings which change the behaviour of a particular view
    calendarFrontendOptions: {
      condensedView: {
        // if true, the resource selection will always be rendered as a dialog
        forceResourceSelectorDialog: false,
        // if true, renders bookables from all resources side-by-side when they overlap in time
        multiResourceMode: false,
      },
    },

    // if set to true and in a multi resource scenario, show an option which loads all bookables of all resources.
    // details are hidden when this option is selected
    allResourcesOption: true,

    // whether the confirmation view is closeable once the booking was successfull.
    allowCloseOnBooking: false,

    // whether the cancel view is closeable once it has been opened. If false, it only closes after previous appointment was cancelled.
    allowCloseOnCancel: false,

    /* 
    Controls where dialogs from the embed are rendered. 
    When enabled, all dialogs are confined to the embed’s designated container (identified via container or containerId) and are clipped/positioned within its bounds, behaving as part of the embed. 
    When disabled, dialogs are rendered at the page level and overlay the entire site, behaving like native site modals. 
    fullScreen respects this setting: with scoping enabled it fills the container; with scoping disabled it fills the browser viewport.
    */
    constrainDialogsToContainer: true,

    // whether 'powered by timum' can be hidden or not. Needs premium plan.
    hideTimumFooter: false,

    // whether bookingjs should hide itself when no customer identifying pData props can be read from the config or the url.
    hiddenForAnonymous: false,

    // send custom field content as comma sperarated, concatenated string via message field to backend
    sendCustomValuesInMessage: false,

    gatherAddressData: false,

    load: 'idle',

    height: 500,

    fields: {
      firstName: {
        index: 0,
        title: 'fields.firstName',
        validation: yup.string().required('validation.field_required'), // <- compare with key in localisation
      },
      lastName: {
        index: 1,
        title: 'fields.lastName',
        validation: yup.string().required('validation.field_required'),
      },
      email: {
        index: 2,
        title: 'fields.email',
        format: 'email',
        type: 'text',
        validation: yup
          .string()
          .email('validation.email_field_must_be_valid')
          .required('validation.field_required'),
      },
      mobile: {
        index: 3,
        title: 'fields.mobile',
        type: 'phoneNumber',
        isRequired: false,
        defaultCountry: 'DE',
        preferredCountries: ['DE', 'CH', 'AT'],
        // validation: is in built and ignored
      },
      message: {
        index: 4,
        title: 'fields.message',
        type: 'textarea',
        validation: yup.string().max(600),
        limit: 600,
      },
      agbs: {
        index: 5,
        title: 'fields.accept_timum_privacy',
        type: 'checkbox',
        validation: yup
          .boolean()
          .required('validation.field_required')
          .test(
            'privacyAccepted',
            'validation.privacy_field_required',
            (value) => value === true,
          ),
      },
      locale: {
        index: 6,
        preventRendering: true,
      },
    },
    interfaces: {
      UnkownBookingPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <ClosableDialogTitle
              onClose={onClose}
              position={constrainDialogsToContainer ? 'left' : 'right'}
            />
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      IdentifiedBookingPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <ClosableDialogTitle
              onClose={onClose}
              position={constrainDialogsToContainer ? 'left' : 'right'}
            />
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },

      // is used by unknownBookingPage and identifiedBookingPage in reaction to a successfull booking
      ConfirmationPage: (props) => {
        const {
          open,
          onClose,
          allowCloseOnBooking,
          constrainDialogsToContainer,
          children,
        } = props;
        return (
          <ScopableDialog
            open={open}
            onClose={() => {
              if (allowCloseOnBooking) {
                onClose();
              }
            }}
            enableScoping={constrainDialogsToContainer}
          >
            {allowCloseOnBooking && (
              <ClosableDialogTitle
                onClose={onClose}
                position={constrainDialogsToContainer ? 'left' : 'right'}
              />
            )}
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },

      // is used by unknownBookingPage when typed in email belongs to an active timum user.
      // (Security feature, preventing unknwons to fill our users calenedars with arbitrary appointmens.)
      LoginPage: ({ open, onClose, constrainDialogsToContainer, children }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
          >
            <DialogContent>{children}</DialogContent>
          </ScopableDialog>
        );
      },
      CancelPage: ({
        open,
        onClose,
        constrainDialogsToContainer,
        allowCloseOnCancel,
        // timeslot,
        // productName,
        // resourceName,
        // onCancelSuccess,
        children,
      }) => {
        return (
          <ScopableDialog
            open={open}
            onClose={() => {
              if (allowCloseOnCancel) {
                onClose();
              }
            }}
            enableScoping={constrainDialogsToContainer}
          >
            {allowCloseOnCancel && (
              <ClosableDialogTitle
                onClose={onClose}
                position={constrainDialogsToContainer ? 'left' : 'right'}
              />
            )}
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="sm" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      ResourcePage: ({
        mobileOpen,
        mobileOnClose,
        constrainDialogsToContainer,
        shouldDisplay,
        height,
        children,
      }) => {
        return (
          <>
            <ScopableDialog
              open={mobileOpen}
              onClose={mobileOnClose}
              enableScoping={constrainDialogsToContainer}
            >
              <DialogContent
                sx={{
                  pl: { xs: 2, sm: 3, md: 4 },
                  pr: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Container maxWidth="sm" sx={{ p: 0 }}>
                  {children}
                </Container>
              </DialogContent>
            </ScopableDialog>
            {shouldDisplay && (
              <Grid
                xs="auto"
                item
                container
                display={{ xs: 'none', sm: 'flex' }}
                wrap="nowrap"
                sx={{
                  mr: 2,
                  maxHeight: height,
                  boxSizing: 'border-box',
                }}
              >
                <Grid item xs="auto">
                  {children}
                </Grid>
                <Grid item xs={1}>
                  <Divider orientation="vertical" variant="middle" />
                </Grid>
              </Grid>
            )}
          </>
        );
      },

      ProductPage: ({
        open,
        onClose,
        children,
        constrainDialogsToContainer,
        requireSelection,
      }) => {
        return (
          <ScopableDialog
            open={open}
            // requireSelection is set when the dialog only opened because the
            // chosen resource dropped the customer's product. Withholding
            // onClose is what actually disables backdrop dismissal in MUI, so
            // picking a product stays the only way out.
            onClose={requireSelection ? undefined : onClose}
            disableEscapeKeyDown={requireSelection}
            enableScoping={constrainDialogsToContainer}
            // bad for assistive technologies
            // but websites jump to auto opened dialog if enabled.
            disableAutoFocus
            disableEnforceFocus
            // Softer, slightly slower fade so the auto-opened product dialog
            // settles in instead of snapping. Fade (not Grow) has no transform
            // scaling, keeping the disableAutoFocus scroll-jump guard effective.
            TransitionComponent={Fade}
            transitionDuration={{ appear: 300, enter: 300, exit: 200 }}
            aria-modal="true"
            role="dialog"
            aria-label="Appointment booking"
          >
            <DialogContent
              sx={{
                pl: { xs: 2, sm: 3, md: 4 },
                pr: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Container maxWidth="md" sx={{ p: 0 }}>
                {children}
              </Container>
            </DialogContent>
          </ScopableDialog>
        );
      },
      ErrorDialog: ({ open, onClose, constrainDialogsToContainer }) => {
        const { t } = useTranslation();
        return (
          <ScopableDialog
            open={open}
            onClose={onClose}
            enableScoping={constrainDialogsToContainer}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {t('reservation_failed.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {t('reservation_failed.message')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} autoFocus>
                Ok
              </Button>
            </DialogActions>
          </ScopableDialog>
        );
      },
      ResourceSwitchConfirmDialog: ({
        open,
        onCancel,
        onConfirm,
        resourceName,
        productName,
        supportingResourceNames = [],
        constrainDialogsToContainer,
      }) => {
        const { t, i18n } = useTranslation();

        // The widget wears the customer's theme, so every value here is derived
        // rather than chosen: the panel fill steps off the paper colour (which
        // self-inverts on dark themes — palette.mode is unreliable, themes are
        // authored as "light" even when their colours are dark), and the held
        // choice is edged in primary because that is what marks a selection
        // everywhere else in the widget, including the resource list behind it.
        const panelSx = (isHeld) => (theme) => ({
          flex: '1 1 11rem',
          minWidth: 0,
          px: 2,
          py: 1.5,
          borderRadius: `${controlRadius(theme)}px`,
          backgroundColor: emphasize(theme.palette.background.paper, 0.04),
          borderLeft: `3px solid ${
            isHeld ? theme.palette.primary.main : theme.palette.divider
          }`,
        });

        const clampSx = {
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        };

        // Small label, distinguished by weight and colour rather than caps —
        // the widget doesn't shout anywhere else.
        const eyebrowSx = {
          display: 'block',
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.5,
          color: 'text.secondary',
        };

        // "Where else can I still get this?" is the question the customer
        // actually has, so name the resources that offer it — bounded, because
        // a channel can hold a lot of them.
        const maxListed = 2;
        const shown = supportingResourceNames.slice(0, maxListed);
        const hidden = supportingResourceNames.length - shown.length;
        const parts =
          hidden > 0
            ? [
                ...shown,
                t('resource_switch_confirm.more_resources', '{{count}} more', {
                  count: hidden,
                }),
              ]
            : shown;
        let offeredAt = parts.join(', ');
        try {
          offeredAt = new Intl.ListFormat(
            i18n?.resolvedLanguage || i18n?.language || undefined,
            { style: 'long', type: 'conjunction' },
          ).format(parts);
        } catch (e) {
          // keep the plain join on an unusable locale tag
        }

        return (
          <ScopableDialog
            open={open}
            onClose={onCancel}
            enableScoping={constrainDialogsToContainer}
            maxWidth="sm"
            fullWidth
            aria-labelledby="resource-switch-confirm-title"
            aria-describedby="resource-switch-confirm-description"
          >
            <DialogTitle
              id="resource-switch-confirm-title"
              // DialogTitle defaults to h6, which this theme uppercases with
              // tracking to match. This title is a sentence, and it has to
              // outrank the panel below it, so it takes h5 and opts out of any
              // transform a customer theme might carry.
              variant="h5"
              sx={{
                pb: 1,
                fontWeight: 600,
                lineHeight: 1.3,
                textTransform: 'none',
                letterSpacing: 'normal',
              }}
            >
              {t(
                'resource_switch_confirm.title',
                'This action cancels your service selection',
              )}
            </DialogTitle>
            <DialogContent
              // centres the content when ScopableDialog goes fullScreen on a
              // narrow container, so a short question doesn't strand itself at
              // the top of a tall sheet
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flexGrow: 1,
                pb: 1,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {/* Deliberately flex-wrap rather than Grid breakpoints: the
                      cells have to fold on the dialog's own width, and a
                      constrained embed can be narrow on a wide viewport. */}
                  <Box
                    id="resource-switch-confirm-description"
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
                  >
                    <Box sx={panelSx(true)}>
                      <Typography component="span" sx={eyebrowSx}>
                        {t(
                          'resource_switch_confirm.current_label',
                          'Currently',
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.4 }}
                      >
                        {productName}
                      </Typography>
                      {parts.length > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={clampSx}
                        >
                          {t(
                            'resource_switch_confirm.offered_at',
                            'Offered at {{resources}}',
                            {
                              resources: offeredAt,
                              interpolation: { escapeValue: false },
                            },
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={panelSx(false)}>
                      <Typography component="span" sx={eyebrowSx}>
                        {t(
                          'resource_switch_confirm.next_label',
                          'If you switch',
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.4, ...clampSx }}
                      >
                        {resourceName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'resource_switch_confirm.next_value',
                          'Choose a service',
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
              {/* The forward action carries the emphasis because the customer
                  just asked for it; the keyboard default is the safe one. */}
              <Button
                onClick={onCancel}
                color="inherit"
                autoFocus
                disableFocusRipple
              >
                {t('resource_switch_confirm.cancel', 'Keep my service')}
              </Button>
              <Button onClick={onConfirm} variant="contained">
                {t('resource_switch_confirm.confirm', 'Proceed anyway')}
              </Button>
            </DialogActions>
          </ScopableDialog>
        );
      },
    },
    callbacks: {},
  },
  muiTheme: {
    palette: {
      primary: {
        main: '#337ab7',
      },
      secondary: { main: '#777' },
      success: {
        main: '#5cb85c',
      },
      warning: { main: '#f0ad4e' },
      error: { main: '#d9534f' },
      info: { main: '#5bc0de' },
    },
    shape: { borderRadius: borderRadius },
    typography: {
      fontFamily: fontFamily,
      fontFaces: [
        {
          family: 'Figtree',
          style: 'normal',
          weight: '100 900',
          display: 'swap',
          src: [
            {
              url: 'https://s3.eu-central-1.amazonaws.com/custom.timum.de/__templates/std_font/Figtree-VariableFont_wght.woff2',
              format: 'woff2',
            },
          ],
        },
        {
          family: 'Figtree',
          style: 'italic',
          weight: '100 900',
          display: 'swap',
          src: [
            {
              url: 'https://s3.eu-central-1.amazonaws.com/custom.timum.de/__templates/std_font/Figtree-Italic-VariableFont_wght.woff2',
              format: 'woff2',
            },
          ],
        },
      ],
      // Override MUI's pxToRem so all internally-generated sizes emit px
      // instead of rem. This makes the widget immune to the host page's
      // root font-size — critical for a shadow-DOM-embedded component
      // that can't control the document-level <html> font-size.
      pxToRem: (size) => `${size}px`,
      fontSize: fontSizeBase,
      h1: {
        fontWeight: 600,
        lineHeight: 1.1,
        fontSize: `${fontSizeH1}px`,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 500,
        lineHeight: 1.15,
        fontSize: `${fontSizeH2}px`,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 500,
        lineHeight: 1.2,
        fontSize: `${fontSizeH3}px`,
        letterSpacing: '-0.005em',
      },
      h4: {
        fontWeight: 500,
        lineHeight: 1.25,
        fontSize: `${fontSizeH4}px`,
      },
      h5: {
        fontWeight: 500,
        lineHeight: 1.3,
        fontSize: `${fontSizeH5}px`,
      },
      h6: {
        fontWeight: 500,
        lineHeight: 1.4,
        fontSize: `${fontSizeH6}px`,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      // body1: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400, // Normal weight for body text
      //   lineHeight: 1.5, // Optimal for reading
      //   fontSize: `${fontSizeBody1}rem`,
      // },
      // body2: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400,
      //   lineHeight: 1.4,
      //   fontSize: `${fontSizeBody2}rem`,
      // },
      // caption: {
      //   fontFamily: fontFamily,
      //   fontWeight: 400,
      //   lineHeight: 1.3,
      //   fontSize: `${fontSizeCaption}rem`,
      //   letterSpacing: '0.03em', // Slight positive tracking for small text
      // },
    },
    components: {
      MuiCssBaseline: {
        // Top-level function form (theme) => ({...}) is REQUIRED: MUI's
        // CssBaseline only resolves the styleOverrides callback when the whole
        // `styleOverrides` is a function. A per-key function value
        // ({ html: ({theme}) => ... }) is NOT resolved — emotion silently drops
        // the un-serialisable function and the rule never emits.
        styleOverrides: (theme) => {
          // Scrollbar colours are DERIVED from the active theme, never hardcoded
          // — otherwise a theme built from dark colours (e.g. the nocturne
          // preset) shows a stark-white scrollbar.
          //
          // This is NOT a workaround for an unreliable palette.mode (mode is
          // now derived correctly — see styles/surfaces.js). It sits BELOW
          // mode on purpose: a binary light/dark flag can only pick between two
          // literal thumb colours, and either one clashes with a tinted or
          // mid-tone track. Deriving from the track itself is the only way to
          // harmonise on an arbitrary background, so do not "simplify" this
          // into a mode branch. Instead:
          //   track = the page background the customer chose (background.page),
          //   thumb = a subtle step off that track via emphasize(), which
          //           darkens a light track or lightens a dark one based on the
          //           track's own luminance — a gentle, self-harmonising
          //           contrast on any theme.
          // Anchored on `html`, NOT `body`: Chrome/Blink does not propagate a
          // scrollbar rule from <body> to the viewport (page) scrollbar — it
          // takes it from the root element — so a `body` rule leaves the big tab
          // scrollbar unstyled in Chrome (Firefox does propagate from body,
          // which is why it worked there). `html` themes the viewport scrollbar
          // in both engines, and `scrollbar-color` still inherits down to every
          // inner scroller (including across the widget's shadow boundary).
          const track =
            theme.palette.background.page ||
            theme.palette.background.default ||
            theme.palette.background.paper;
          // emphasize() parses the colour in JS, so it needs a real colour.
          // On the CssVars path palette values are `var(--…)` strings it can't
          // parse — guard so an unparseable track skips the scrollbar override
          // rather than throwing and taking the rest of CssBaseline down with it.
          let thumb, thumbActive;
          try {
            thumb = emphasize(track, 0.2);
            thumbActive = emphasize(track, 0.4);
          } catch {
            return {};
          }
          return {
            html: {
              scrollbarColor: `${thumb} ${track}`,
              '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                backgroundColor: track,
              },
              '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                borderRadius: 8,
                backgroundColor: thumb,
                minHeight: 8,
                width: 3,
                border: `5px solid ${track}`,
              },
              '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                {
                  backgroundColor: thumbActive,
                },
              '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                {
                  backgroundColor: thumbActive,
                },
              '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                {
                  backgroundColor: thumbActive,
                },
              '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
                backgroundColor: track,
              },
            },
          };
        },
      },
      MuiDialog: {
        defaultProps: {
          hideBackdrop: true,
          maxWidth: 'md',
          fullWidth: true,
        },
        styleOverrides: {
          paper: {
            boxShadow:
              '0px 0px 8px 0px rgba(0,0,0,0.08), 0px 0px 16px 2px rgba(0,0,0,0.05)',
            outline: '1px solid rgba(0,0,0,0.08)',
          },
        },
      },
      // borderRadius overrides removed for Button, Paper, OutlinedInput —
      // those components read from theme.shape.borderRadius natively.
      // ListItem and ListItemButton do NOT read shape.borderRadius in MUI v6,
      // so they need explicit overrides. Using a callback to read from the
      // theme dynamically, so postMessage-triggered theme changes take effect.
      MuiButton: {
        styleOverrides: {
          // Callback so control radius reads from the theme dynamically
          // (postMessage-triggered theme changes take effect). Falls back to
          // shape.borderRadius when controlRadius is unset — byte-identical to
          // the previous native Button radius.
          root: ({ theme }) => ({
            textTransform: 'inherit',
            boxShadow: 'none',
            borderRadius: controlRadius(theme),
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => outlinedInputOutline(theme),
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h2: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h3: {
            // marginTop: lineHeightComputed,
            // marginBottom: lineHeightComputed / 2,
          },
          h4: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
          h5: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
          h6: {
            // marginTop: lineHeightComputed / 2,
            // marginBottom: lineHeightComputed / 2,
          },
        },
      },
    },
  },
  fcConfig: {
    initialView: 'timeGridWeek',
    useCustomTimumCss: true,
    displayEventEnd: false,
    scrollTimeReset: false,
    scrollTime: '09:00:00',
    slotLabelInterval: '00:30:00',
    slotLabelFormat: { hour: 'numeric', minute: '2-digit' },
    views: {
      timeGridWeek: {
        slotMinTime: '07:00:00',
        slotMaxTime: '21:00:00',
        allDaySlot: false,
      },
    },
  },
};

// var customerFieldsNativeFormats = {
//   email: {
//     format: 'email',
//   },
//   comment: {
//     format: 'textarea',
//   },
//   phone: {
//     format: 'tel',
//   },
// };

// Preset: timeDateFormat = '24h-dmy-mon'
// var timeDateFormat24hdmymon = {
//   ui: {
//     booking_date_format: 'D. MMMM YYYY',
//     booking_time_format: 'HH:mm',
//   },
//   fullcalendar: {
//     timeFormat: 'HH:mm',
//     firstDay: 1,
//     views: {
//       basic: {
//         columnFormat: 'dddd D.M.',
//       },
//       agenda: {
//         columnFormat: 'ddd D.M.',
//         slotLabelFormat: 'HH:mm',
//       },
//     },
//   },
// };

// Preset: timeDateFormat = '12h-mdy-sun'
// var timeDateFormat12hmdysun = {
//   ui: {
//     booking_date_format: 'MMMM D, YYYY',
//     booking_time_format: 'h:mma',
//   },
//   fullcalendar: {
//     timeFormat: 'h:mma',
//     firstDay: 0,
//     views: {
//       basic: {
//         columnFormat: 'dddd M/D',
//       },
//       agenda: {
//         columnFormat: 'ddd M/D',
//         slotLabelFormat: 'h:mma',
//       },
//     },
//   },
// };

// Preset: availabilityView = 'agendaWeek'
// var availabilityViewAgendaWeek = {
//   fullcalendar: {
//     header: {
//       left: '',
//       center: '',
//       right: 'today, prev, next',
//     },
//     defaultView: 'agendaWeek',
//   },
// };

// Preset: availabilityView = 'listing'
// var availabilityViewListing = {
//   fullcalendar: {
//     header: {
//       left: '',
//       center: '',
//       right: '',
//     },
//     defaultView: 'listing',
//   },
// };
